import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ENABLE_MSW } from 'configs';
import React from 'react';
import ReactDOM from 'react-dom/client';
import AppRouterProvider from 'routes/AppRouterProvider';
import { setupInterceptors } from 'utils/axios';
import { initNetworkMonitor } from 'utils/networkMonitor';
import AntdConfigProvider from './AntdConfigProvider';
import App from './App';
import './index.css';
import './locales/i18n';
import FontWrapper from './FontWrapper';

setupInterceptors({ debug: false });

// Initialize network monitor
initNetworkMonitor({
  enableNotifications: true,
  notificationDuration: 4.5,
  debounceDelay: 1000,
});

const enableMock = async () => {
  if (!ENABLE_MSW) return;
  const { worker } = await import('./mocks/browser');
  return worker.start();
};

Sentry.init({
  dsn: 'https://5d07fab4f80e21b4cfa4afd0bd7a4d02@o4507888315203584.ingest.us.sentry.io/4508637127049216',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  debug: import.meta.env.MODE === 'development',
  release: import.meta.env.VITE_APP_VERSION || '1.0.0',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

enableMock().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AntdConfigProvider>
        <QueryClientProvider client={queryClient}>
          <AppRouterProvider>
            <FontWrapper>
              <App />
            </FontWrapper>
          </AppRouterProvider>
        </QueryClientProvider>
      </AntdConfigProvider>
    </React.StrictMode>,
  );
});

// FIXME: find the use of this
// Hot reloadable translation json files
/* if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
} */
